import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import {
  ColorPickerWrapper,
  ColorArea,
  ColorThumb,
  SpectrumSlider,
  SpectrumThumb,
  ColorInputWrapper,
  InputLabel
} from './styles';
import Input from '../input';

interface ColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value = '#000000', onChange }) => {
  const [colorState, setColorState] = useState(() => {
    const rgb = hexToRGB(value);
    const hsv = RGBtoHSV(rgb.r, rgb.g, rgb.b);
    return {
      hue: hsv.h,
      saturation: hsv.s,
      brightness: hsv.v,
      hexValue: value
    };
  });

  const colorAreaRef = useRef<HTMLDivElement>(null);
  const spectrumRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const rafRef = useRef<number>();

  // Memoize color conversion functions
  const updateColor = useCallback(
    (h: number, s: number, v: number) => {
      const rgb = HSVtoRGB(h, s, v);
      const hex = RGBtoHex(rgb.r, rgb.g, rgb.b);
      setColorState((prev) => ({ ...prev, hexValue: hex }));
      onChange?.(hex);
    },
    [onChange]
  );

  const handleColorChange = useCallback(
    (h: number, s: number, v: number) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        setColorState((prev) => ({
          ...prev,
          hue: h,
          saturation: s,
          brightness: v
        }));
        updateColor(h, s, v);
      });
    },
    [updateColor]
  );

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const handleColorAreaMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;

      const handleMove = (e: MouseEvent | TouchEvent) => {
        if (!colorAreaRef.current) return;
        const rect = colorAreaRef.current.getBoundingClientRect();

        // Use clientX/Y for mouse events, touches[0].clientX/Y for touch events
        const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        const clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;

        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

        const newSaturation = x * 100;
        const newBrightness = 100 - y * 100;

        handleColorChange(colorState.hue, newSaturation, newBrightness);
      };

      const handleMouseUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMove); // Remove touchmove
        document.removeEventListener('touchend', handleMouseUp); // Remove touchend
        document.removeEventListener('touchcancel', handleMouseUp); // Remove touchcancel
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMove); // Add touchmove
      document.addEventListener('touchend', handleMouseUp); // Add touchend
      document.addEventListener('touchcancel', handleMouseUp); // Add touchcancel

      // Handle the initial event (either mouse or touch)
      handleMove(e.nativeEvent as MouseEvent | TouchEvent);
    },
    [colorState.hue, handleColorChange]
  );

  const handleSpectrumMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;

      const handleMove = (e: MouseEvent | TouchEvent) => {
        if (!spectrumRef.current) return;
        const rect = spectrumRef.current.getBoundingClientRect();

        // Use clientX for mouse events, touches[0].clientX for touch events
        const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;

        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const newHue = x * 360;

        handleColorChange(newHue, colorState.saturation, colorState.brightness);
      };

      const handleMouseUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMove); // Remove touchmove
        document.removeEventListener('touchend', handleMouseUp); // Remove touchend
        document.removeEventListener('touchcancel', handleMouseUp); // Remove touchcancel
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMove); // Add touchmove
      document.addEventListener('touchend', handleMouseUp); // Add touchend
      document.addEventListener('touchcancel', handleMouseUp); // Add touchcancel

      // Handle the initial event (either mouse or touch)
      handleMove(e.nativeEvent as MouseEvent | TouchEvent);
    },
    [colorState.saturation, colorState.brightness, handleColorChange]
  );

  const handleHexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newHex = e.target.value;
      if (/^#[0-9A-F]{6}$/i.test(newHex)) {
        const rgb = hexToRGB(newHex);
        const hsv = RGBtoHSV(rgb.r, rgb.g, rgb.b);
        setColorState({
          hue: hsv.h,
          saturation: hsv.s,
          brightness: hsv.v,
          hexValue: newHex
        });
        onChange?.(newHex);
      } else {
        setColorState((prev) => ({ ...prev, hexValue: newHex }));
      }
    },
    [onChange]
  );

  // Memoize style objects
  const thumbStyle = useMemo(
    () => ({
      left: `${colorState.saturation}%`,
      top: `${100 - colorState.brightness}%`
    }),
    [colorState.saturation, colorState.brightness]
  );

  const spectrumThumbStyle = useMemo(
    () => ({
      left: `${(colorState.hue / 360) * 100}%`
    }),
    [colorState.hue]
  );

  return (
    <ColorPickerWrapper>
      <ColorArea
        ref={colorAreaRef}
        hue={colorState.hue}
        onMouseDown={handleColorAreaMouseDown}
        onTouchStart={handleColorAreaMouseDown}>
        <ColorThumb style={thumbStyle} />
      </ColorArea>

      <SpectrumSlider ref={spectrumRef} onMouseDown={handleSpectrumMouseDown} onTouchStart={handleSpectrumMouseDown}>
        <SpectrumThumb style={spectrumThumbStyle} />
      </SpectrumSlider>

      <ColorInputWrapper>
        <InputLabel>HEX</InputLabel>
        <Input
          value={colorState.hexValue}
          onChange={handleHexChange}
          placeHolder='#000000'
          fieldWrapperWidth='100%'
          className='input-field'
        />
      </ColorInputWrapper>
    </ColorPickerWrapper>
  );
};

// Color conversion utilities
const HSVtoRGB = (h: number, s: number, v: number) => {
  s = s / 100;
  v = v / 100;
  const i = Math.floor(h / 60);
  const f = h / 60 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let r = 0,
    g = 0,
    b = 0;
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

const RGBtoHex = (r: number, g: number, b: number) => {
  return `#${[r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('')}`;
};

const hexToRGB = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : { r: 0, g: 0, b: 0 };
};

const RGBtoHSV = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max === min) {
    h = 0;
  } else {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: h * 360,
    s: s * 100,
    v: v * 100
  };
};

export default memo(ColorPicker);
